import { Button } from "@/components/ui/button/button";
import { DollarSendIcon } from "@/components/ui/icons";
import { cn, currencyFormatter } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Zap } from "lucide-react";
import { FieldErrors, useForm } from "react-hook-form";
import DepositHeader from "@/pages/Home/deposit/_components/DepositHeader";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form/form";
import { BankAccount } from "@/api/bank-accounts";
import { Checkbox } from "@/components/ui/checkbox";
import AmountInputBox from "@/components/ui/amountInputBox";
import { createWithdrawSchema, WithdrawFormData } from "../amount.schema";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { getTermsOfServiceUrl } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button/button-variants";
import {
	centsToDollars,
	dollarsToCents,
} from "@/lib/number-conversion/number-conversion.utils";
import { addBusinessDays } from "date-fns";
import { useState } from "react";
import { MAX_TRANSFER_OUT } from "@/constants/common-constants";

interface WithdrawPageProps {
	balance: number;
	onContinue: (amount: number) => void;
	selectedAccount: BankAccount;
	onBack: () => void;
	onClose: () => void;
}

const FORM_ID = "withdraw-form" as const;

const SelectWithdrawAmount: React.FC<WithdrawPageProps> = ({
	balance,
	onContinue,
	selectedAccount,
	onBack,
	onClose,
}) => {
	const [shake, setShake] = useState(false);
	const balanceInDollars = centsToDollars(balance);
	const maxTransferAmount = Math.min(balanceInDollars, MAX_TRANSFER_OUT);

	const methods = useForm<WithdrawFormData>({
		resolver: zodResolver(createWithdrawSchema(maxTransferAmount)),
		mode: "onBlur",
		defaultValues: {
			amount: "",
			paymentMethod: "standard",
			termsAccepted: false,
		},
	});

	const triggerShake = () => {
		setShake(true);
		setTimeout(() => setShake(false), 500);
		if (navigator.vibrate) {
			navigator.vibrate(200);
		}
	};

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		trigger,
		formState: { errors },
	} = methods;

	const selectedPaymentMethod = watch("paymentMethod");
	const enteredAmount = watch("amount");

	const onSubmit = (data: WithdrawFormData) => {
		const amount = Number.parseFloat(data.amount);
		const amountInCents = dollarsToCents(amount);

		onContinue(amountInCents);
	};

	const onError = (errors: FieldErrors<WithdrawFormData>) => {
		console.log("Form validation errors:", errors);
	};

	return (
		<div className="flex flex-col justify-between kashu-container py-4">
			<div className="pt-4">
				<DepositHeader
					title="Choose Amount"
					onBack={onBack}
					onClose={onClose}
				/>
				<Form {...methods}>
					<form
						id={FORM_ID}
						onSubmit={handleSubmit(onSubmit, onError)}
						className="w-full"
					>
						<div className="items-center w-full mt-4 text-center">
							<FormField
								control={control}
								name="amount"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<div
												className={cn(
													"transition-transform",
													shake ? "animate-shake" : "",
												)}
											>
												<AmountInputBox
													price={field.value}
													disabled={balance === 0}
													setPrice={field.onChange}
													onBlur={async () => {
														const valid = await trigger("amount");
														if (!valid) triggerShake();
													}}
													type="tel"
													inputMode="decimal"
													data-testid="amount-input"
													onKeyDown={(e) => {
														if (e.key === "Enter") {
															e.preventDefault();
														}
													}}
												/>
											</div>
										</FormControl>
										<FormMessage className="text-center" />
									</FormItem>
								)}
							/>
						</div>
						<div className="mt-1 mb-4 text-sm text-textLight text-center flex justify-center items-center gap-1">
							<span>Transfer up to</span>
							{currencyFormatter.format(maxTransferAmount)}
						</div>
						<div className="flex items-center justify-center w-full gap-4">
							<Popover>
								<PopoverTrigger>
									<Button
										type="button"
										variant="unstyled"
										size="compact"
										className="flex gap-0 flex-col items-center justify-center p-2 rounded-lg w-[180px] max-w-[300px] h-[200px] cursor-pointer bg-primary/10 text-gray-600/50"
									>
										<Zap className="" strokeWidth={1.5} size={64} />
										<h2 className="mt-2 text-lg font-bold">Instant</h2>
										<p className="mt-1 text-sm text-center">within an hour</p>
									</Button>
								</PopoverTrigger>
								<PopoverContent className="flex justify-center text-md text-textDark">
									Coming Soon
								</PopoverContent>
							</Popover>
							<Button
								variant="unstyled"
								className={`flex gap-0 flex-col items-center justify-center p-2 border border-primary-600 rounded-lg w-[180px] max-w-[300px] h-[200px] cursor-pointer [&_svg]:size-16 ${
									selectedPaymentMethod === "standard"
										? "border-2 bg-primary-50"
										: ""
								}`}
								type="button"
								onClick={() =>
									setValue("paymentMethod", "standard", {
										shouldValidate: true,
									})
								}
							>
								<DollarSendIcon className="w-24 h-24 fill-primary-600" />
								<h2 className="mt-2 text-lg font-bold truncate text-textDark">
									Up to 3 Biz Days
								</h2>
								<p className="mt-1 text-sm text-center text-textLight">
									Estimated by
									<br />
									{addBusinessDays(new Date(), 3).toLocaleDateString(
										undefined,
										{
											weekday: "long",
											month: "short",
											day: "numeric",
										},
									)}
								</p>
							</Button>
						</div>

						{errors.paymentMethod && (
							<p className="mt-2 text-sm text-center text-red-500">
								{errors.paymentMethod.message}
							</p>
						)}

						<div className="w-full mt-4">
							{selectedPaymentMethod === "instant" && (
								<>
									<div className="flex items-center justify-between w-full">
										<p className="text-sm text-textLight">Fee</p>
										<p className="text-sm font-bold text-textDark">$400</p>
									</div>
									<div className="w-full h-[1px] bg-gray-200 my-2" />
								</>
							)}
							<div className="flex items-center justify-between w-full">
								<p className="text-sm text-textLight">Transfer to</p>
								<p className="text-sm font-bold text-textDark">
									{selectedAccount.bank_name ?? ""}
									{"  "}
									{selectedAccount.bank_account?.replace(/\*/g, "")}
								</p>
							</div>
							<div className="w-full h-[1px] bg-gray-200 my-2" />
						</div>

						<div className="flex items-center w-full gap-2 mt-4">
							<FormField
								control={control}
								name="termsAccepted"
								rules={{ required: true }}
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-row items-center space-x-2 space-y-0">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="ml-2 text-sm">
												<span className="text-textLight">
													By submitting payment below I have read and agree to{" "}
												</span>
												<span className="font-bold text-textDark">
													<a
														target="_blank"
														rel="noreferrer"
														className={buttonVariants({
															variant: "link",
															size: "compact",
														})}
														href={getTermsOfServiceUrl()}
													>
														Terms & Conditions.
													</a>
												</span>
											</FormLabel>
										</div>
										<FormMessage className="mt-1 text-sm" />
									</FormItem>
								)}
							/>
						</div>
						<p className="text-sm font-medium text-textLight text-center mt-4 mb-4">
							<b>Best Practice:</b> Please avoid submitting duplicate
							transactions to the same bank account within 24 hours.
						</p>

						<Button
							form={FORM_ID}
							type="submit"
							className="w-full py-4 mt-4 text-base font-bold text-white bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Withdraw{" "}
							{Number.isNaN(Number.parseFloat(enteredAmount))
								? null
								: currencyFormatter.format(Number.parseFloat(enteredAmount))}
						</Button>
					</form>
				</Form>
			</div>
		</div>
	);
};

export default SelectWithdrawAmount;
